import React, { useCallback, useEffect, useState } from 'react';
import {
  cloutReviewsApi,
  InviteResult,
  InviteResultStatus,
  SentInvite,
} from '../../services/cloutReviews';
import { getApiErrorMessage } from '../../utils/errors';

const MAX_EMAILS = 10;

const RESULT_LABELS: Record<InviteResultStatus, string> = {
  sent: 'Invite sent',
  already_invited: 'Already invited',
  already_reviewed: 'Already reviewed you',
  own_email: "That's your own email",
  daily_limit: 'Daily limit reached, try tomorrow',
  failed: "Couldn't send, try again later",
};

const INVITE_LABELS: Record<SentInvite['status'], string> = {
  pending: 'Not used yet',
  used: 'Review submitted',
  expired: 'Expired',
};

export const parseEmails = (input: string): string[] =>
  Array.from(
    new Set(
      input
        .split(/[\s,;]+/)
        .map((email) => email.trim())
        .filter(Boolean)
    )
  );

// Lets a provider email clients a one-time link to review them on CLOUT
const ReviewInvites: React.FC = () => {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const [invites, setInvites] = useState<SentInvite[]>([]);

  const loadInvites = useCallback(
    () =>
      cloutReviewsApi
        .listInvites()
        .then(setInvites)
        .catch(() => {
          // The list is a convenience; sending still works without it
        }),
    []
  );

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const emails = parseEmails(input);
    if (emails.length === 0) return setError('Enter at least one email address.');
    if (emails.length > MAX_EMAILS)
      return setError(`Send up to ${MAX_EMAILS} invites at a time.`);

    setSending(true);
    setError(null);
    try {
      setResults(await cloutReviewsApi.sendInvites(emails));
      setInput('');
      loadInvites();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send the invites.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mb-8 font-['Open_Sans'] text-sm text-[#1f1f1f]">
      <h2 className="font-['Ovo'] text-2xl uppercase mb-2">Invite clients to review you</h2>
      <p className="text-[#767472] mb-3">
        We'll email each client a one-time link. Invite clients whatever their experience: CLOUT
        publishes negative reviews too, and every review is checked before it appears.
      </p>

      <form onSubmit={handleSend} className="space-y-2">
        <label htmlFor="invite-emails" className="block font-semibold">
          Client email addresses
        </label>
        <textarea
          id="invite-emails"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={3}
          placeholder="client@example.com, another@example.com"
          className="w-full px-4 py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
        />
        <p className="text-xs text-[#767472]">
          Up to {MAX_EMAILS} at a time, separated by commas or new lines. Only invite people who
          agreed to hear from you.
        </p>
        <button
          type="submit"
          disabled={sending}
          className="px-4 py-2.5 bg-[#c6a664] text-white font-semibold uppercase text-xs hover:bg-[#b89854] disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send invites'}
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-2 text-red-600">
          {error}
        </p>
      )}

      {results && (
        <ul className="mt-3 space-y-1" aria-label="Invite results">
          {results.map((result) => (
            <li key={result.email}>
              {result.email}: {RESULT_LABELS[result.status] ?? result.status}
            </li>
          ))}
        </ul>
      )}

      {invites.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold mb-1">Sent invites</h3>
          <ul className="space-y-1 text-[#767472]">
            {invites.map((invite) => (
              <li key={invite.id}>
                {invite.email}: {INVITE_LABELS[invite.status]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ReviewInvites;
